import { useState, type ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { formatMcpClient } from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { buildCustomerMcpLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  formatMcpMemberName,
  groupMcpMembersByCustomer,
  type PortfolioMcpMember,
} from "@/v2-backoffice-client/portfolio/portfolio.mcp.utils"

export type PortfolioMcpPeopleTableProps = ComponentProps<"div"> & {
  /** Every person with a tool call, with their customer. */
  members: PortfolioMcpMember[]
}

const CLIENT_LIST_LIMIT = 3

// The report's appendix: every person who has called the MCP, grouped by
// customer (busiest customer first, busiest person first within it), with
// calls since the first, the last 28 and 7 days, days with a call, first
// and last call and the AI clients. A name opens the person's panel; a
// customer row opens the customer's Activity tab. Folded by default: the
// summary line says how many people at how many customers.
function PortfolioMcpPeopleTable(props: PortfolioMcpPeopleTableProps) {
  const { members, className, ...divProps } = props

  const [search, setSearch] = useState("")
  const searchTerm = search.trim().toLowerCase()

  const matching =
    searchTerm === ""
      ? members
      : members.filter(
          (member) =>
            formatMcpMemberName(member).toLowerCase().includes(searchTerm) ||
            member.CustomerName.toLowerCase().includes(searchTerm) ||
            (member.Email ?? "").toLowerCase().includes(searchTerm),
        )
  const groups = groupMcpMembersByCustomer(matching)

  return (
    <div className={cn("flex flex-col gap-2", className)} {...divProps}>
      <div className="w-56 self-end">
        <Input
          type="search"
          size="xs"
          placeholder="Search people or customers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {groups.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nobody matches the search.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="usage-report-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>E-mail</th>
                <th className="num" title="Tool calls since the first one">
                  Calls total
                </th>
                <th className="num">Last 28 d</th>
                <th className="num">Last 7 d</th>
                <th className="num" title="Days with at least one tool call">
                  Active days
                </th>
                <th>First call</th>
                <th>Last call</th>
                <th>AI clients</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <PortfolioMcpPeopleGroup
                  key={group.externalCustomerId}
                  group={group}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function PortfolioMcpPeopleGroup(props: {
  group: ReturnType<typeof groupMcpMembersByCustomer>[number]
}) {
  const { group } = props
  return (
    <>
      <tr className="group">
        <td colSpan={2}>
          <Link
            to={buildCustomerMcpLink(group.externalCustomerId)}
            iconSrc={null}
            className="font-medium"
          >
            {group.customerName}
          </Link>
          <span className="ml-2 font-normal text-muted-foreground">
            {group.members.length}{" "}
            {group.members.length === 1 ? "person" : "people"}
          </span>
        </td>
        <td className="num">{formatNumber(group.callsTotal)}</td>
        <td className="num">
          {group.calls28d > 0 ? formatNumber(group.calls28d) : "—"}
        </td>
        <td colSpan={5} />
      </tr>
      {group.members.map((member) => (
        <tr key={member.UserId}>
          <td className="pl-4">
            <Link
              to={`?v=user:${member.UserId}&user-view-tabs-tab=usage`}
              iconSrc={null}
              className="block max-w-56 truncate"
            >
              {formatMcpMemberName(member)}
            </Link>
          </td>
          <td className="max-w-56 truncate text-muted-foreground">
            {member.Email ?? "—"}
          </td>
          <td className="num">{formatNumber(member.CallsTotal)}</td>
          <td className="num">
            {member.Calls28d > 0 ? (
              formatNumber(member.Calls28d)
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
          <td className="num">
            {member.Calls7d > 0 ? (
              formatNumber(member.Calls7d)
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
          <td className="num">{member.ActiveDays}</td>
          <td className="whitespace-nowrap text-muted-foreground">
            {member.FirstActiveAt
              ? formatDateString(member.FirstActiveAt)
              : "—"}
          </td>
          <td className="whitespace-nowrap text-muted-foreground">
            {member.LastActiveAt ? (
              <RelativeDate date={member.LastActiveAt} />
            ) : (
              "—"
            )}
          </td>
          <td>
            {member.Clients.length > 0
              ? member.Clients.slice(0, CLIENT_LIST_LIMIT)
                  .map(formatMcpClient)
                  .join(", ")
              : "—"}
          </td>
        </tr>
      ))}
    </>
  )
}

export default PortfolioMcpPeopleTable
